import React from "react";
import {
  Box,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CallMerge as MergeIcon, Language as LanguageIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import { StatusChip } from "../../../components/status";
import ErrorMessage from "../../../components/ErrorMessage";
import { INDIGO, SLATE } from "../../../constants/Colors";
import { parseUrl, formatIndexedTimestamp } from "../../../utils";
import { URL_COL_TEMPLATE } from "./urlSidebarConstants";

/**
 * A single URL row in the URL table sidebar.
 *
 * @param {object} url - The URL object
 * @param {boolean} isChecked - Whether the row checkbox is checked
 * @param {function} onCheckboxChange - Handler called with (e, id) on checkbox change
 * @param {function} onRefreshDaysChange - Handler called with (url, e) after debounce
 * @param {object} refreshDaysRef - Ref used for debounce timer
 * @param {number} defaultRefreshDay - Placeholder value for refresh days input
 * @param {function} onViewChunks - Handler called with (url) when View button is clicked
 * @param {number} idx - Row index for alternating backgrounds
 * @param {number} totalCount - Total row count for border logic
 * @param {boolean} showSourceDomain - Whether to show source domain badge (default false)
 * @param {string[]} inheritedTags - Tags inherited from the website (default [])
 * @param {function} onTagsChange - Handler called with (url, newTags) when URL-level tags change
 */
export default function UrlTableRow({
  url,
  isChecked,
  onCheckboxChange,
  onRefreshDaysChange,
  refreshDaysRef,
  defaultRefreshDay,
  onViewChunks,
  idx,
  totalCount,
  showSourceDomain = false,
  readOnly = false,
  inheritedTags = [],
  onTagsChange,
}) {
  const { domain, path } = parseUrl(url.url);
  const sourceDomain = url.website_url ? parseUrl(url.website_url).domain : null;
  const ownTags = (url.tags || []).filter((t) => !inheritedTags.includes(t));

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: URL_COL_TEMPLATE,
        px: 3,
        py: 1.25,
        alignItems: "center",
        borderBottom: idx < totalCount - 1 ? `1px solid ${SLATE[100]}` : "none",
        bgcolor: isChecked ? INDIGO[50] : idx % 2 === 0 ? "#ffffff" : SLATE[50],
        "&:hover": { bgcolor: isChecked ? "#e8ecff" : "#eef0f8" },
        transition: "background-color 0.12s",
      }}
    >
      {/* Checkbox */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {!readOnly && (
          <Checkbox
            size="small"
            checked={isChecked}
            onChange={(e) => onCheckboxChange(e, url.id)}
            sx={{ p: 0, color: SLATE[300], "&.Mui-checked": { color: INDIGO[500] } }}
          />
        )}
      </Box>

      {/* URL + error */}
      <Box sx={{ minWidth: 0, pr: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <Typography
            component="a"
            href={url.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: "0.8125rem", textDecoration: "none", lineHeight: 1.4, wordBreak: "break-all", minWidth: 0, "&:hover span.url-path": { textDecoration: "underline" } }}
          >
            <Box component="span" sx={{ color: SLATE[400], fontWeight: 400 }}>{domain}</Box>
            <Box component="span" className="url-path" sx={{ color: INDIGO[600], fontWeight: 500 }}>{path === "/" ? "/" : `/${path.replace(/^\//, "")}`}</Box>
          </Typography>
          {url.metadata?.merge_content && (
            <Tooltip title="Merge Content Enabled — this URL's content is merged with its source" arrow placement="top" PopperProps={{ sx: { zIndex: 21474837 } }}>
              <MergeIcon sx={{ fontSize: "0.9375rem", color: "#1565c0", flexShrink: 0, opacity: 0.8 }} />
            </Tooltip>
          )}
        </Box>
        {showSourceDomain && sourceDomain && sourceDomain !== domain && (
          <Box sx={{ mt: "4px", display: "inline-flex", alignItems: "center", gap: "3px", px: "6px", py: "1px", bgcolor: SLATE[100], borderRadius: "4px" }}>
            <LanguageIcon sx={{ fontSize: "0.6875rem", color: SLATE[400] }} />
            <Typography sx={{ fontSize: "0.6875rem", color: SLATE[500] }}>{sourceDomain}</Typography>
          </Box>
        )}
        {url.metadata?.internal_url && (
          <Typography
            component="a"
            href={url.metadata.internal_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: "0.75rem", color: INDIGO[500], textDecoration: "underline", display: "block", mt: "4px" }}
          >
            Download
          </Typography>
        )}
        <ErrorMessage message={url?.metadata?.error_message} />
      </Box>

      {/* Tags */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center", pr: 2, minWidth: 0 }}>
        {inheritedTags.map((tag) => (
          <Tooltip
            key={`inherited-${tag}`}
            title="Inherited from the website — edit it on the website row"
            arrow
            placement="top"
            PopperProps={{ sx: { zIndex: 21474837 } }}
          >
            <Chip
              label={tag}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6875rem",
                bgcolor: SLATE[100],
                color: SLATE[500],
                border: `1px solid ${SLATE[200]}`,
                borderRadius: "6px",
                "& .MuiChip-label": { px: "6px" },
              }}
            />
          </Tooltip>
        ))}

        {readOnly ? (
          ownTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6875rem",
                bgcolor: INDIGO[50],
                color: INDIGO[600],
                border: `1px solid ${INDIGO[100]}`,
                borderRadius: "6px",
                "& .MuiChip-label": { px: "6px" },
              }}
            />
          ))
        ) : (
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={[]}
            value={ownTags}
            onChange={(_, val) => onTagsChange(url, val)}
            sx={{ flex: 1, minWidth: 90 }}
            renderTags={(val, getTagProps) =>
              val.map((opt, i) => (
                <Chip
                  key={opt}
                  label={opt}
                  size="small"
                  {...getTagProps({ index: i })}
                  sx={{
                    height: 20,
                    fontSize: "0.6875rem",
                    bgcolor: INDIGO[50],
                    color: INDIGO[700],
                    "& .MuiChip-label": { px: "6px" },
                    "& .MuiChip-deleteIcon": { color: INDIGO[400] },
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={ownTags.length === 0 ? "Add tags..." : ""}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    fontSize: "0.75rem",
                    bgcolor: SLATE[50],
                    py: "1px",
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: SLATE[300] },
                    "&.Mui-focused fieldset": { borderColor: INDIGO[400] },
                  },
                }}
              />
            )}
          />
        )}
      </Box>

      {/* Refresh days */}
      <Box>
        <TextField
          size="small"
          type="number"
          placeholder={`${defaultRefreshDay ?? "—"}`}
          defaultValue={url.refresh_days || ""}
          inputProps={{ min: 1 }}
          disabled={readOnly}
          onChange={(e) => {
            if (readOnly) return;
            if (refreshDaysRef.current) clearTimeout(refreshDaysRef.current);
            refreshDaysRef.current = setTimeout(() => onRefreshDaysChange(url, e), 500);
          }}
          sx={{
            width: 80,
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              fontSize: "0.8125rem",
              bgcolor: SLATE[50],
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: SLATE[300] },
              "&.Mui-focused fieldset": { borderColor: INDIGO[400] },
            },
            "& input": { py: "5px", px: "8px", textAlign: "center", color: SLATE[700] },
          }}
        />
      </Box>

      {/* Status */}
      <Box>
        <StatusChip status={url.status} />
        {url.indexed_at && (
          <Typography sx={{ mt: "5px", fontSize: "0.6875rem", color: SLATE[400] }}>
            {formatIndexedTimestamp(url.indexed_at)}
          </Typography>
        )}
      </Box>

      {/* Chunks */}
      <Box>
        <Button
          size="small"
          variant="text"
          startIcon={<VisibilityIcon sx={{ fontSize: "0.875rem !important" }} />}
          onClick={() => onViewChunks(url)}
          sx={{
            color: SLATE[500],
            textTransform: "none",
            borderRadius: "6px",
            fontSize: "0.75rem",
            py: "3px",
            px: "8px",
            minWidth: "unset",
            "&:hover": { color: INDIGO[600], bgcolor: INDIGO[50] },
          }}
        >
          View
        </Button>
      </Box>
    </Box>
  );
}
